import {Injectable, inject, signal, computed} from '@angular/core';
import {NavigationCancel, NavigationEnd, NavigationError, Router} from '@angular/router';
import {ApiService} from './api.service';
import {OnboardingState, User, UserRole} from '../interfaces/api-interfaces';

export type TourStepMode = 'fullscreen' | 'spotlight' | 'interstitial';
export type TourAdvanceMode = 'button' | 'targetClick' | 'timer';

export interface TourStep {
    id: string;
    phase: 0 | 1 | 2;
    mode: TourStepMode;
    /** Route the tour navigates to when this step becomes active (only if not already there). */
    navigateTo?: string;
    /** CSS selector (usually a data-tour attribute) of the element to spotlight. */
    target?: string;
    title?: string;
    text: string;
    advanceOn: TourAdvanceMode;
    /** Seconds before the "Weiter" button becomes enabled (advanceOn: 'timer'/'button' combo, e.g. welcome video). */
    minDelaySeconds?: number;
    skippable: boolean;
    buttonLabel?: string;
}

// Vimeo ID + privacy hash for the Phase-0 welcome video (unlisted video —
// the hash is required or the player embed 403s, same as videoUrl usage in
// video-player.service.ts). Source: https://vimeo.com/1210774126/0ba11f15b4
export const ONBOARDING_WELCOME_VIDEO_ID = '1210774126';
export const ONBOARDING_WELCOME_VIDEO_HASH = '0ba11f15b4';

const STEPS: TourStep[] = [
    {
        id: 'p0-welcome', phase: 0, mode: 'fullscreen',
        title: 'Hey, schön, dass du da bist!',
        text: 'Eine kurze persönliche Begrüßung, bevor es losgeht.',
        advanceOn: 'button', minDelaySeconds: 5, skippable: false, buttonLabel: 'Weiter',
    },
    {
        id: 'p0-avatar', phase: 0, mode: 'fullscreen',
        title: 'Wir reden gerne mit Menschen, nicht mit Buchstaben.',
        text: 'Lade ein Profilbild hoch, damit man dich in der Community erkennt.',
        advanceOn: 'button', skippable: false, buttonLabel: 'Weiter',
    },
    {
        id: 'p0-bio', phase: 0, mode: 'fullscreen',
        title: 'Zwei Sätze über dich',
        text: 'Erzähl kurz, wer du bist — das erscheint direkt auf deinem Profil.',
        advanceOn: 'button', skippable: false, buttonLabel: 'Weiter',
    },
    {
        id: 'p1-cosmos', phase: 1, mode: 'spotlight', navigateTo: '/home',
        target: '[data-tour="cosmos-card"]',
        text: 'Du bist jetzt Teil einer Bewegung. Hier findest du Podcast, Homepage und unsere Social-Kanäle.',
        advanceOn: 'button', skippable: true, buttonLabel: 'Weiter',
    },
    {
        id: 'p1-community', phase: 1, mode: 'spotlight', navigateTo: '/home',
        target: '[data-tour="qa-community"]',
        text: 'Das ist deine Community — der Treffpunkt für alle FreyMuT-Menschen, nicht nur für deinen Kurs. Hier findest du z.B. "News & Nährstoffe" für Neuigkeiten und "Austausch & Vernetzung", um andere kennenzulernen.',
        advanceOn: 'button', skippable: true, buttonLabel: 'Weiter',
    },
    {
        id: 'p1-nextevent', phase: 1, mode: 'spotlight', navigateTo: '/home',
        target: '[data-tour="next-event-card"]',
        text: 'Leuchtfeuercafé und Themenabende kannst du ab sofort besuchen — auch bevor dein Kurs offiziell startet.',
        advanceOn: 'button', skippable: true, buttonLabel: 'Weiter',
    },
    {
        id: 'p1-interstitial', phase: 1, mode: 'interstitial',
        text: '',
        advanceOn: 'button', skippable: false,
    },
    {
        id: 'p2-nextstep', phase: 2, mode: 'spotlight', navigateTo: '/home',
        target: '[data-tour="next-step-card"]',
        text: 'Hier siehst du immer sofort, wo du weitermachen kannst — dein persönlicher Fortschritt.',
        advanceOn: 'button', skippable: true, buttonLabel: 'Weiter',
    },
    {
        id: 'p2-lernen', phase: 2, mode: 'spotlight',
        target: '[data-tour="nav-learning"]',
        text: 'Alle Module deines Kurses, inklusive Fortschritt. Manches ist Pflicht fürs Zertifikat — das wird automatisch abgehakt. In manchen Kursen öffnet sich die nächste Lektion erst, wenn du die vorherige abgeschlossen hast. Klicke auf "Lernen", um weiterzugehen.',
        advanceOn: 'targetClick', skippable: true,
    },
    {
        id: 'p2-materialien', phase: 2, mode: 'spotlight',
        target: '[data-tour="nav-materials"]',
        text: 'Alle Skripte, PDFs und Unterlagen deines Kurses — durchsuchbar, an einem Ort. Klicke auf "Materialien", um weiterzugehen.',
        advanceOn: 'targetClick', skippable: true,
    },
    {
        id: 'p2-benachrichtigungen', phase: 2, mode: 'spotlight', navigateTo: '/settings/notifications',
        target: '[data-tour="notification-settings"]',
        text: 'Hier stellst du ein, worüber du informiert werden willst — pro Channel einzeln, plus wie oft E-Mails kommen sollen: sofort, einmal täglich gesammelt, oder gar keine.',
        advanceOn: 'button', skippable: true, buttonLabel: 'Weiter',
    },
    {
        // Highest-value step for support load: most tickets that could've been a
        // 30-second question in this channel land in the inbox instead, because
        // nobody knows it exists.
        id: 'p2-technische-hilfe', phase: 2, mode: 'spotlight', navigateTo: '/community',
        target: '[data-tour-support-channel]',
        text: 'Technisches Problem oder eine Frage zur Plattform? Bitte nicht per E-Mail — hier im Kanal "Technische Hilfe" bekommst du am schnellsten Antwort. Klicke drauf, um weiterzugehen.',
        advanceOn: 'targetClick', skippable: true,
    },
    {
        // Last step by design: the tour ends in the class channel, not on a settings
        // page — clicking the channel both finishes the tour and takes the user
        // straight into their own community space.
        id: 'p2-klassenraum', phase: 2, mode: 'spotlight', navigateTo: '/community',
        target: '[data-tour-class-channel]',
        text: 'Das ist dein Klassenraum — hier tauschst du dich nur mit deiner eigenen Kursgruppe aus, anders als bei den großen Kanälen von eben. Klicke darauf, um deine Tour abzuschließen und direkt reinzuschauen.',
        advanceOn: 'targetClick', skippable: true,
    },
];

function defaultState(): OnboardingState {
    return {status: 'not_started', phase: 0, stepId: null, phase1Choice: null};
}

@Injectable({providedIn: 'root'})
export class TourService {
    private readonly apiService = inject(ApiService);
    private readonly router = inject(Router);

    private readonly state = signal<OnboardingState>(defaultState());
    private initialized = false;
    private targetWaitAbort: (() => void) | null = null;

    /** Null when the tour isn't currently showing anything (hidden / resume-hint state). */
    readonly activeStep = signal<TourStep | null>(null);
    readonly targetRect = signal<DOMRect | null>(null);

    /** Dashboard shows a dezent "Tour fortsetzen" hint when this is true. */
    readonly resumeHintVisible = computed(() => {
        const s = this.state();
        return s.status === 'in_progress' && s.phase1Choice === 'later' && this.activeStep() === null;
    });

    constructor() {
        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.reanchorCurrentStep();
            }
        });
    }

    /**
     * Called once from App.getAuthMe() after the backend profile (with the real
     * `onboarding` state) has merged into the user — NOT from the raw JWT-derived
     * user, which has no `onboarding` field and would make every login look like
     * a fresh start (clobbering a completed/in-progress state back to step one).
     */
    startFromUser(user: User) {
        if (this.initialized) {
            return;
        }
        this.initialized = true;
        const isTeilnehmer = (user.roles || []).includes(UserRole.TEILNEHMER);
        if (!isTeilnehmer) {
            return;
        }
        const onboarding = user.onboarding ?? defaultState();
        this.state.set(onboarding);
        if (onboarding.status === 'completed') {
            return;
        }
        if (onboarding.phase1Choice === 'later') {
            // Paused after Phase 1 on a previous visit — show the "Tour fortsetzen"
            // hint instead of re-opening the mid-flow interstitial on every reload.
            this.activeStep.set(null);
            return;
        }
        const resumeStepId = onboarding.stepId ?? this.firstStepIdOfPhase(onboarding.phase);
        this.goToStep(resumeStepId, {persist: onboarding.stepId === null});
    }

    private firstStepIdOfPhase(phase: 0 | 1 | 2): string {
        return STEPS.find(s => s.phase === phase)!.id;
    }

    private stepIndex(id: string): number {
        return STEPS.findIndex(s => s.id === id);
    }

    private goToStep(id: string, opts: {persist: boolean} = {persist: true}) {
        const step = STEPS.find(s => s.id === id);
        if (!step) {
            this.complete();
            return;
        }
        this.activeStep.set(step);
        this.targetRect.set(null);

        this.state.update(s => ({...s, phase: step.phase, stepId: step.id, status: 'in_progress'}));
        if (opts.persist) {
            this.persist();
        }

        if (step.mode === 'spotlight') {
            this.navigateAndAnchor(step);
        }
    }

    private navigateAndAnchor(step: TourStep) {
        if (!step.navigateTo) {
            this.anchorToTarget(step);
            return;
        }
        // A targetClick advance (e.g. clicking the "Lernen" nav item) triggers the
        // element's own routerLink navigation at the same time we're reacting to the
        // click. Wait for that navigation to settle before firing our own, so the two
        // don't race and cancel each other.
        this.afterCurrentNavigationSettles(() => {
            if (this.router.url.split('?')[0] !== step.navigateTo) {
                this.router.navigateByUrl(step.navigateTo!).then(() => this.anchorToTarget(step));
            } else {
                this.anchorToTarget(step);
            }
        });
    }

    private afterCurrentNavigationSettles(cb: () => void) {
        if (!this.router.getCurrentNavigation()) {
            cb();
            return;
        }
        const sub = this.router.events.subscribe(e => {
            if (e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError) {
                sub.unsubscribe();
                cb();
            }
        });
    }

    private reanchorCurrentStep() {
        const step = this.activeStep();
        if (step && step.mode === 'spotlight') {
            // Give Angular a tick to render the new route's DOM before searching.
            setTimeout(() => this.anchorToTarget(step), 50);
        }
    }

    private anchorToTarget(step: TourStep) {
        this.targetWaitAbort?.();
        if (!step.target) {
            return;
        }

        const deadline = Date.now() + 4000;
        let settled = false;
        const tryResolve = () => {
            if (settled) return;
            const el = this.resolveVisibleElement(step.target!);
            if (el) {
                settled = true;
                observer.disconnect();
                // Several targets (Cosmos card, next-event card, class channel) can sit
                // below the fold inside a nested scroll container — bring them into view
                // before measuring, then measure after layout settles.
                el.scrollIntoView({block: 'center', behavior: 'auto'});
                requestAnimationFrame(() => this.targetRect.set(el.getBoundingClientRect()));
            } else if (Date.now() > deadline) {
                settled = true;
                observer.disconnect();
                clearInterval(poll);
                // Target never appeared (e.g. no upcoming event, no class assigned yet) —
                // don't leave the user stuck on a spotlight with nothing to highlight.
                this.advance();
            }
        };

        const observer = new MutationObserver(tryResolve);
        observer.observe(document.body, {childList: true, subtree: true, attributes: true});
        this.targetWaitAbort = () => {
            settled = true;
            observer.disconnect();
        };

        const poll = setInterval(() => {
            tryResolve();
            if (settled) clearInterval(poll);
        }, 150);
        tryResolve();
    }

    private resolveVisibleElement(selector: string): HTMLElement | null {
        const el = document.querySelector<HTMLElement>(selector);
        if (!el) return null;
        return el.offsetParent !== null ? el : null;
    }

    /** Called by TourOverlayComponent on window resize/scroll to keep the spotlight aligned. */
    refreshTargetRect() {
        const step = this.activeStep();
        if (step?.target) {
            const el = this.resolveVisibleElement(step.target);
            if (el) this.targetRect.set(el.getBoundingClientRect());
        }
    }

    /** Global click listener registered once by TourOverlayComponent (document, bubble phase). */
    handleGlobalClick(target: EventTarget | null) {
        const step = this.activeStep();
        if (!step || step.advanceOn !== 'targetClick' || !step.target || !(target instanceof Element)) {
            return;
        }
        const targetEl = document.querySelector(step.target);
        if (targetEl && (target === targetEl || targetEl.contains(target))) {
            // Defer past the click's own routerLink navigation call (see
            // afterCurrentNavigationSettles) instead of racing it.
            setTimeout(() => this.advance(), 0);
        }
    }

    /** "Weiter"/"Fertig" button on the bubble, or the Phase 0 wizard buttons. */
    advance() {
        const step = this.activeStep();
        if (!step) return;
        const nextIndex = this.stepIndex(step.id) + 1;
        const next = STEPS[nextIndex];
        if (!next) {
            this.complete();
            return;
        }
        this.goToStep(next.id);
    }

    skip() {
        const step = this.activeStep();
        if (!step?.skippable) return;
        this.advance();
    }

    chooseContinueNow() {
        this.state.update(s => ({...s, phase1Choice: null}));
        this.advance();
    }

    chooseLater() {
        this.state.update(s => ({...s, phase1Choice: 'later'}));
        this.persist();
        this.activeStep.set(null);
        this.targetRect.set(null);
    }

    resumeFromHint() {
        this.state.update(s => ({...s, phase1Choice: null}));
        this.goToStep(this.firstStepIdOfPhase(2));
    }

    private complete() {
        this.activeStep.set(null);
        this.targetRect.set(null);
        this.state.update(s => ({...s, status: 'completed', stepId: null}));
        this.persist();
    }

    private persist() {
        const onboarding = this.state();
        this.apiService.updateUserProfile({onboarding} as Partial<User> as User).subscribe({
            error: (err) => console.error('Failed to persist onboarding tour state:', err),
        });
    }
}
